import * as THREE from "three";
import { HubZoom } from "./hubZoom";
import { HubInsets, HubLayout } from "./hubLayout";
import type { SceneRig } from "./scene";
import { HubGestures } from "../input/hubGestures";

/**
 * The hub scene's camera side (split from hubScene.ts): framing the still shot, the player's pinch zoom over it
 * (hubZoom.ts) and the canvas gestures that drive it (input/hubGestures.ts), and the chips that follow the shot
 * (`onLayout`). HubScene owns one and calls it at its re-frames, once a frame and on dispose.
 */
export class HubSceneZoom {
  private readonly zoom: HubZoom;
  private readonly gestures: HubGestures;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly rig: SceneRig) {
    this.zoom = new HubZoom(rig.iso);
    this.gestures = new HubGestures(rig.canvas, rig);
  }

  /** Calls `fn` whenever the shot moves (a re-frame, a pinch, a pan); returns the unsubscribe. */
  onLayout(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Tells the chips the shot moved. */
  notify(): void { for (const fn of this.listeners) fn(); }

  /**
   * Frames `points` in the band between the insets as the default shot, with the player's zoom set aside and then put
   * back over it. `inShot` gets the band as the default shot sees it — on a copy of its camera, so a later pinch does
   * not change the answer (the pets keep to the default shot's lawn, views/petHub.ts).
   */
  frame(points: readonly THREE.Vector3[], insets: HubInsets, w: number, h: number, groundY: number, inShot: (f: (x: number, z: number) => boolean) => void): void {
    this.zoom.release();
    HubLayout.frame(this.rig.iso, points, insets, w, h);
    inShot(HubLayout.inBand(this.rig.iso.camera.clone(), groundY, insets, w, h));
    this.zoom.capture();
  }

  /** Once a frame after the camera's update: a pinch or a pan moved the view, so the chips pinned to the models follow it. */
  follow(): void { if (this.zoom.moved()) this.notify(); }

  /** Whether a pinch or a drag on the scene is under way or just ended: a click on a chip now is part of it, not a tap. */
  get busy(): boolean { return this.gestures.busy; }

  dispose(): void {
    this.listeners.clear();
    this.gestures.dispose();
    this.zoom.dispose();
  }
}
